import * as React from 'react';
import {createSmartContext} from '@packages/utilities/smart-context';
import {
  get as getPersistentState,
  observeKey,
  unobserveKey,
} from '@packages/systems/designer/persistent-state';
import {DEFAULT_FRAME_ORIENTATION} from '@packages/systems/component-canvas/types';
import type {
  FrameOrientationSettings,
  ComponentCanvasUISettings,
} from '@packages/systems/component-canvas/types';

type ComponentCanvasSettingsContextValue = ComponentCanvasUISettings;

const initialContextValue: ComponentCanvasSettingsContextValue = {};

const {ContextProvider, useContext: useComponentCanvasSettingsContext} =
  createSmartContext<ComponentCanvasSettingsContextValue>(initialContextValue);

/**
 * Provider that manages component canvas settings from persistent UI state.
 * Observes persistent state changes and provides them via SmartContext.
 */
export const ComponentCanvasSettingsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [componentCanvasUiSettings, setComponentCanvasUiSettings] =
    React.useState<ComponentCanvasUISettings>(() => {
      const persistentState = getPersistentState();
      return persistentState.componentCanvasUISettings ?? {};
    });

  React.useEffect(() => {
    const listener = () => {
      const persistentState = getPersistentState();
      setComponentCanvasUiSettings(
        persistentState.componentCanvasUISettings ?? {}
      );
    };

    observeKey('componentCanvasUISettings', listener);

    return () => {
      unobserveKey('componentCanvasUISettings', listener);
    };
  }, []);

  return (
    <ContextProvider value={componentCanvasUiSettings}>
      {children}
    </ContextProvider>
  );
};

/**
 * Hook to get frame orientation for a specific component.
 */
export const useComponentCanvasFrameOrientation = (
  componentId: string | null | undefined
): FrameOrientationSettings => {
  const selector = React.useCallback(
    (context: ComponentCanvasSettingsContextValue) => {
      if (!componentId) return DEFAULT_FRAME_ORIENTATION.mode;
      return (
        context[componentId]?.frameOrientation.mode ??
        DEFAULT_FRAME_ORIENTATION.mode
      );
    },
    [componentId]
  );

  const mode = useComponentCanvasSettingsContext(selector);

  // As this value will be consumed at a level wrapping Canvas,
  // return a stable object that only changes when the mode changes
  return React.useMemo(
    (): FrameOrientationSettings => ({
      mode,
      gap: DEFAULT_FRAME_ORIENTATION.gap,
    }),
    [mode]
  );
};
